/**
 * Componente interativo dos cards de curso da Celso Lisboa.
 *
 * Cada instância controla o próprio estado em Light DOM. As únicas integrações
 * globais preservadas são os serviços compartilhados de cupom e matrícula.
 */
if (window.customElements && !customElements.get('celso-course-card')) {
  class CelsoCourseCard extends HTMLElement {
    constructor() {
      super();

      this.config = null;
      this.initialized = false;
      this.listenersAttached = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleEnrollmentBeforeAction = this.handleEnrollmentBeforeAction.bind(this);
    }

    connectedCallback() {
      this.attachListeners();

      if (!this.initialized) {
        this.hydrateConfig();
        this.setDefaultModalidade();
        this.initialized = true;
      } else if (!this.config) {
        this.hydrateConfig();
      }

      this.applyState();
    }

    disconnectedCallback() {
      this.detachListeners();
    }

    attachListeners() {
      if (this.listenersAttached) return;

      this.addEventListener('click', this.handleClick);
      this.addEventListener('cl:enrollment-cta:before-action', this.handleEnrollmentBeforeAction);
      this.listenersAttached = true;
    }

    detachListeners() {
      if (!this.listenersAttached) return;

      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('cl:enrollment-cta:before-action', this.handleEnrollmentBeforeAction);
      this.listenersAttached = false;
    }

    hydrateConfig() {
      const configElement = this.querySelector('script[data-celso-shelf-config]');
      const productId = String(this.dataset.productId || '');

      if (!configElement) {
        this.config = window.CelsoShelfConfigs?.[productId] || null;
        return this.config;
      }

      try {
        const config = JSON.parse((configElement.textContent || '').trim());
        config.productId = String(config.productId || productId);
        this.config = config;
        this.registerConfig(config);
      } catch (error) {
        console.warn('Não foi possível carregar a configuração do card de curso:', error);
        this.config = window.CelsoShelfConfigs?.[productId] || null;
      }

      return this.config;
    }

    registerConfig(config) {
      if (!config?.productId) return;

      const productId = String(config.productId);
      window.CelsoShelfConfigs = window.CelsoShelfConfigs || {};
      window.CelsoShelfConfigs[productId] = config;

      if (window.EnrollPopupRegistry && typeof window.EnrollPopupRegistry.register === 'function') {
        window.EnrollPopupRegistry.register(config);
        return;
      }

      window.EnrollPopupPreConfigs = window.EnrollPopupPreConfigs || [];
      const existingIndex = window.EnrollPopupPreConfigs.findIndex((item) => {
        return item && String(item.productId) === productId;
      });

      if (existingIndex >= 0) {
        window.EnrollPopupPreConfigs[existingIndex] = config;
      } else {
        window.EnrollPopupPreConfigs.push(config);
      }
    }

    setDefaultModalidade(force = false) {
      const buttons = this.querySelectorAll('.modalidade-btn');
      const firstButton = buttons[0];
      if (!firstButton) return;
      if (!force && this.querySelector('.modalidade-btn.is-active')) return;

      buttons.forEach((button) => button.classList.remove('is-active'));
      firstButton.classList.add('is-active');
    }

    getActiveModalidadeButton() {
      return this.querySelector('.modalidade-btn.is-active') || this.querySelector('.modalidade-btn');
    }

    getActiveVariant() {
      const config = this.config || this.hydrateConfig();
      if (!config?.variants?.length) return null;
      if (this.dataset.shelfCardType === 'pro') return config.variants[0];

      const button = this.getActiveModalidadeButton();
      if (!button?.dataset.variantId) return config.variants[0];

      return CelsoCourseCard.findVariant(config, button.dataset.variantId) || config.variants[0];
    }

    getCouponState(variantId, couponCodeBase = '') {
      if (!variantId) return null;

      let selector = `[data-shelf-cupom-state][data-variant-id="${String(variantId)}"]`;
      if (couponCodeBase) {
        selector += `[data-cupom-code-base="${String(couponCodeBase)}"]`;
      }

      return this.querySelector(selector);
    }

    getCouponOptions(couponApi) {
      const urlCoupon =
        typeof couponApi.getCupomFromUrl === 'function' ? (couponApi.getCupomFromUrl() || '').trim() : '';

      if (urlCoupon) {
        return { allowImplicit: false, candidates: [urlCoupon] };
      }

      const sectionId = this.dataset.shelfTwoSection || '';
      const sectionCandidates = sectionId
        ? window.__celsoShelfTwoDiscountBySection?.[sectionId]
        : null;

      if (Array.isArray(sectionCandidates) && sectionCandidates.length) {
        return { allowImplicit: false, candidates: sectionCandidates.slice() };
      }

      return {
        allowImplicit: true,
        candidates:
          typeof couponApi.getCouponCandidates === 'function' ? couponApi.getCouponCandidates() || [] : [],
      };
    }

    shouldHideCouponBadges() {
      if (this.dataset.hideCouponBadges === 'true') return true;
      return Boolean(this.closest('[data-hide-coupon-badges="true"]'));
    }

    isPosEadActive() {
      if (!this.classList.contains('course-card--pos')) return false;

      const button = this.getActiveModalidadeButton();
      if (!button) return false;

      const modalidade = (button.dataset.modalidadeOption || '').toLowerCase();
      return (
        modalidade.includes('ead') ||
        modalidade.includes('distância') ||
        modalidade.includes('distancia')
      );
    }

    getPosEadInstallments() {
      const installments = Number.parseInt(this.dataset.posEadParcelas, 10);
      return installments > 0 ? installments : 12;
    }

    setStandardPrice(cents, compareCents) {
      const posEad = this.isPosEadActive();
      const installments = posEad ? this.getPosEadInstallments() : 1;
      const displayCents = posEad ? Math.round(cents / installments) : cents;
      const formatted = CelsoCourseCard.formatMoney(displayCents);
      const integerElement = this.querySelector('.price-int');
      const centsElement = this.querySelector('.price-cents');
      const prefixElement = this.querySelector('.course-card__price-prefix');
      const installmentsElement = this.querySelector('.course-card__price-installments');

      if (integerElement) integerElement.textContent = formatted.integer;
      if (centsElement) centsElement.textContent = formatted.decimals;

      this.classList.toggle('course-card--pos-ead-installments', posEad);

      const isGraduacaoOrPos =
        this.classList.contains('course-card--graduacao') || this.classList.contains('course-card--pos');
      const isLivre = this.classList.contains('course-card--livre');
      const compareValue = Number(compareCents) || 0;

      if (compareValue > cents) {
        const formattedCompare = CelsoCourseCard.formatMoney(compareValue);

        if (prefixElement) {
          prefixElement.innerHTML =
            'De <span class="price-prefix-currency">R$</span><span class="price-prefix-value">' +
            formattedCompare.integer +
            ',' +
            formattedCompare.decimals +
            '</span> por';
          prefixElement.style.display = 'block';
        }

        if (installmentsElement) {
          if (posEad) {
            installmentsElement.textContent = `${installments}x`;
            installmentsElement.style.display = 'block';
          } else {
            installmentsElement.style.display = 'none';
          }
        }
        return;
      }

      if (installmentsElement) {
        if (posEad) {
          installmentsElement.textContent = `${installments}x`;
          installmentsElement.style.display = 'block';
        } else {
          installmentsElement.style.display = 'none';
        }
      }

      if (posEad && prefixElement) {
        prefixElement.style.display = 'none';
      } else if (isGraduacaoOrPos && prefixElement) {
        prefixElement.innerHTML = 'A partir de';
        prefixElement.style.display = 'block';
      } else if (isLivre && prefixElement) {
        prefixElement.innerHTML = '';
        prefixElement.style.display = 'none';
      } else if (prefixElement) {
        prefixElement.style.display = 'none';
      }
    }

    setProPrice(cents) {
      const formatted = CelsoCourseCard.formatMoney(cents);
      const integerElement = this.querySelector('.course-card__button-price-integer');
      const decimalsElement = this.querySelector('.course-card__button-price-decimals');

      if (integerElement) integerElement.textContent = formatted.integer;
      if (decimalsElement) decimalsElement.textContent = `,${formatted.decimals}`;
    }

    setCouponBadge(badge, visible, firstCents, codeBase) {
      if (!badge) return;
      if (this.shouldHideCouponBadges()) visible = false;

      if (!visible) {
        badge.hidden = true;
        badge.setAttribute('hidden', '');
        badge.setAttribute('aria-hidden', 'true');
        this.removeAttribute('data-has-cupom');
        return;
      }

      const couponApi = window.CelsoCupom;
      const formatted = CelsoCourseCard.formatMoney(firstCents);
      const firstPrice =
        couponApi && typeof couponApi.formatarDinheiro === 'function'
          ? couponApi.formatarDinheiro(firstCents)
          : `${formatted.integer},${formatted.decimals}`;
      const priceElement = badge.querySelector('.celso-discount-badge-price');
      const codeElement = badge.querySelector('.celso-discount-badge-code');
      const middleElement = badge.querySelector('.celso-discount-badge__mid');

      if (priceElement) priceElement.textContent = firstPrice;
      if (codeElement) codeElement.textContent = codeBase || '';
      if (middleElement) middleElement.style.display = '';

      badge.removeAttribute('hidden');
      badge.hidden = false;
      badge.style.display = '';
      badge.style.visibility = '';
      badge.setAttribute('aria-hidden', 'false');
      this.setAttribute('data-has-cupom', 'true');
    }

    resolveDisplayCents(variant) {
      const priceCents = Number(variant?.price) || 0;
      const compareCents = Number(variant?.compare_at_price) || 0;
      const result = {
        priceCents,
        compareCents,
        hasCoupon: false,
        resolved: null,
        firstCents: null,
        couponCodeBase: '',
      };
      const couponApi = window.CelsoCupom;

      if (this.shouldHideCouponBadges() || !variant || !couponApi) return result;

      const couponOptions = this.getCouponOptions(couponApi);
      const resolved =
        typeof couponApi.resolveEffectiveCouponForVariant === 'function'
          ? couponApi.resolveEffectiveCouponForVariant(variant, couponOptions)
          : typeof couponApi.resolveBestCouponForVariant === 'function'
            ? couponApi.resolveBestCouponForVariant(variant, couponOptions.candidates, couponOptions)
            : null;

      if (!resolved?.entry) return result;

      result.hasCoupon = true;
      result.resolved = resolved;
      result.couponCodeBase =
        resolved.displayCode ||
        (typeof couponApi.getCupomBase === 'function'
          ? couponApi.getCupomBase(resolved.code || '')
          : resolved.code || '');

      const state = this.getCouponState(variant.id, result.couponCodeBase);
      if (state) {
        const first = Number(state.dataset.firstCents);
        if (Number.isFinite(first) && first >= 0) result.firstCents = first;
      }

      if (!result.firstCents && typeof couponApi.resolverValorPrimeiraMensalidade === 'function') {
        const firstInstallment = couponApi.resolverValorPrimeiraMensalidade(variant, resolved.entry);
        if (firstInstallment && Number.isFinite(firstInstallment.valorCentavos)) {
          result.firstCents = firstInstallment.valorCentavos;
        }
      }

      if (typeof couponApi.resolverRatioPrecoExibicaoCupom === 'function') {
        const ratioInfo = couponApi.resolverRatioPrecoExibicaoCupom(resolved.entry);
        const basePrice =
          typeof couponApi.obterPrecoBaseDaVariante === 'function'
            ? Number(couponApi.obterPrecoBaseDaVariante(variant))
            : priceCents;

        if (
          ratioInfo?.ratio > 0 &&
          ratioInfo.ratio < 1 &&
          Number.isFinite(basePrice) &&
          basePrice > 0
        ) {
          result.priceCents = Math.round(basePrice * ratioInfo.ratio);
        }
      }

      if (!Number.isFinite(result.priceCents) || result.priceCents <= 0) {
        result.priceCents = priceCents;
      }

      return result;
    }

    applyState() {
      const variant = this.getActiveVariant();
      if (!variant) return;

      const badge = this.querySelector('.celso-discount-badge--shelf');
      const isPro = this.dataset.shelfCardType === 'pro';
      const posEad = this.isPosEadActive();

      if (this.shouldHideCouponBadges()) {
        this.setCouponBadge(badge, false);
        if (isPro) {
          this.setProPrice(Number(variant.price) || 0);
        } else {
          this.setStandardPrice(Number(variant.price) || 0, Number(variant.compare_at_price) || 0);
        }
        return;
      }

      const display = this.resolveDisplayCents(variant);

      if (display.hasCoupon) {
        let shelfPriceCents = display.priceCents;
        const variantPrice = Number(variant.price) || 0;
        if (!Number.isFinite(shelfPriceCents) || shelfPriceCents <= 0) {
          shelfPriceCents = variantPrice;
        }

        if (display.firstCents) {
          this.setCouponBadge(badge, true, display.firstCents, display.couponCodeBase);
        } else if (badge && posEad && shelfPriceCents > 0 && shelfPriceCents < variantPrice) {
          badge.hidden = false;
          badge.removeAttribute('hidden');
          badge.setAttribute('aria-hidden', 'false');
          this.setAttribute('data-has-cupom', 'true');
        } else {
          this.setCouponBadge(badge, false);
        }

        if (isPro) {
          this.setProPrice(shelfPriceCents);
        } else {
          this.setStandardPrice(shelfPriceCents, display.compareCents);
        }
        return;
      }

      this.setCouponBadge(badge, false);
      if (isPro) {
        this.setProPrice(Number(variant.price) || 0);
      } else {
        this.setStandardPrice(Number(variant.price) || 0, Number(variant.compare_at_price) || 0);
      }
    }

    persistModalidade(modalidade, productId) {
      if (!modalidade) return;

      try {
        localStorage.setItem('modalidadeSelecionada', modalidade);
      } catch (error) {
        console.warn('Não foi possível salvar a modalidade selecionada:', error);
      }

      window.dispatchEvent(
        new CustomEvent('modalidadeSelecionada', {
          detail: { value: modalidade, source: 'card', productId },
        })
      );
    }

    updateEnrollmentData(modalidade) {
      if (!modalidade) return;

      let data = {};
      try {
        const storedData = localStorage.getItem('dadosMatricula');
        const parsedData = storedData ? JSON.parse(storedData) : {};
        if (parsedData && typeof parsedData === 'object') data = parsedData;
      } catch (error) {
        console.warn('Não foi possível ler dados de matrícula salvos:', error);
      }

      if (data.modalidade === modalidade) return;
      data.modalidade = modalidade;

      try {
        localStorage.setItem('dadosMatricula', JSON.stringify(data));
      } catch (error) {
        console.warn('Não foi possível atualizar dados de matrícula:', error);
      }

      window.dispatchEvent(new CustomEvent('dadosMatriculaAtualizados', { detail: { dados: data } }));
    }

    getEnrollmentSelection(trigger, modalidadeRequired) {
      const productId = String(trigger?.dataset.productId || this.dataset.productId || '');
      const activeButton = this.querySelector('.modalidade-btn.is-active');
      const modalidade = activeButton?.dataset.modalidadeOption || '';

      return {
        productId,
        modalidade,
        valid: Boolean(productId && (!modalidadeRequired || modalidade)),
      };
    }

    saveEnrollmentSelection(selection) {
      if (!selection.modalidade) return;
      this.persistModalidade(selection.modalidade, selection.productId);
      this.updateEnrollmentData(selection.modalidade);
    }

    openEnrollment(productId) {
      if (window.EnrollPopupRegistry && typeof window.EnrollPopupRegistry.open === 'function') {
        window.EnrollPopupRegistry.open(productId || undefined);
        return true;
      }

      if (typeof window.abrirPopupMatricula === 'function') {
        window.abrirPopupMatricula(productId || undefined);
        return true;
      }

      return false;
    }

    handleEnrollmentBeforeAction(event) {
      const detail = event.detail || {};
      const trigger = detail.trigger;

      if (
        detail.action !== 'direct_enroll' ||
        !trigger ||
        !this.contains(trigger) ||
        !trigger.classList.contains('course-card__cta')
      ) {
        return;
      }

      const selection = this.getEnrollmentSelection(trigger, detail.modalidadeRequired);
      if (!selection.valid) {
        event.preventDefault();
        return;
      }

      this.saveEnrollmentSelection(selection);
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      if (!target) return;

      const proButton = target.closest('.course-card__button-pro');
      if (proButton && this.contains(proButton)) {
        const productId = String(proButton.dataset.productId || this.dataset.productId || '');
        if (!productId && proButton.tagName.toLowerCase() === 'a') return;

        event.preventDefault();
        this.openEnrollment(productId);
        return;
      }

      const ctaButton = target.closest('.course-card__cta');
      if (ctaButton && this.contains(ctaButton)) {
        if (ctaButton.closest('cl-enrollment-cta')) return;

        const selection = this.getEnrollmentSelection(
          ctaButton,
          ctaButton.dataset.modalidadeRequired === 'true'
        );

        event.preventDefault();
        if (!selection.valid) return;

        this.saveEnrollmentSelection(selection);
        this.openEnrollment(selection.productId);
        return;
      }

      const modalidadeButton = target.closest('.modalidade-btn');
      if (modalidadeButton && this.contains(modalidadeButton)) {
        event.preventDefault();

        this.querySelectorAll('.modalidade-btn').forEach((button) => {
          button.classList.remove('is-active');
        });
        modalidadeButton.classList.add('is-active');
        this.applyState();
        return;
      }

      const media = target.closest('.midia-card-product');
      if (!media || !this.contains(media)) return;
      if (target.closest('button, a')) return;

      const url = media.dataset.productUrl;
      if (!url) return;

      event.preventDefault();
      window.location.href = url;
    }

    static findVariant(config, variantId) {
      const id = String(variantId);
      return config?.variants?.find((variant) => String(variant.id) === id) || null;
    }

    static formatMoney(cents) {
      const formatted = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Number(cents) || 0) / 100);
      const parts = formatted.split(',');

      return {
        integer: (parts[0] || '0').replace(/\./g, ''),
        decimals: (parts[1] || '00').slice(0, 2),
      };
    }
  }

  customElements.define('celso-course-card', CelsoCourseCard);
}
